/**
 * Centralized Form Utilities
 * Provides reusable form validation, state management, and submission utilities
 */

import {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import {ApplicationError} from '../../types/ApplicationError';
import StandardizedErrorHandler from '../../Services/common/StandardizedErrorHandler';

// Form validation types
export interface FormField {
  name: string;
  value: any;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  customValidator?: (value: any) => string | null;
}

export interface FormFieldProps {
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
}

export interface FormValidationRules {
  [key: string]: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    customValidator?: (value: any) => string | null;
  };
}

export interface FormState<T = any> {
  data: T;
  errors: Record<string, string>;
  isSubmitting: boolean;
  isDirty: boolean;
  isValid: boolean;
}

export interface FormActions<T = any> {
  setField: (field: string, value: any) => void;
  setFields: (fields: Partial<T>) => void;
  setError: (field: string, error: string) => void;
  setErrors: (errors: Record<string, string>) => void;
  clearErrors: () => void;
  clearField: (field: string) => void;
  reset: () => void;
  validate: () => boolean;
  validateField: (field: string) => boolean;
  submit: (onSubmit: (data: T) => Promise<void>) => Promise<void>;
}

/**
 * Custom hook for form state management
 */
export function useFormState<T extends Record<string, any>> (
  initialData: T,
  validationRules?: FormValidationRules
): [FormState<T>, FormActions<T>] {
  const [data, setData] = useState<T>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const initialDataRef = useRef(initialData);

  // Update dirty state when data changes - disabled to prevent infinite loops
  // TODO: Implement proper dirty state tracking without infinite loops
  // useEffect(() => {
  //   const hasChanges = JSON.stringify(data) !== JSON.stringify(initialDataRef.current);
  //   setIsDirty(hasChanges);
  // }, [data]);

  // Validate form when data or errors change
  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    setIsValid(!hasErrors);
  }, [errors]);

  // Don't auto-validate on form load - only validate on user interaction or submission

  const setField = useCallback((field: string, value: any) => {
    // Handle arrays specially - don't convert them to strings
    if (Array.isArray(value)) {
      setData(prev => ({...prev, [field]: value}));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[field];
          return newErrors;
        });
      }
      return;
    }

    // Always extract primitive values from objects
    let safeValue = value;

    if (value && typeof value === 'object') {
      // If it's a DOM element, extract the value
      if (value.nodeType || value.constructor?.name?.includes('HTML')) {
        safeValue = value.value || value.textContent || '';
      } else if (value.value !== undefined) {
        // For objects with a value property (like select options), use the value
        safeValue = value.value;
      } else if (value.label !== undefined) {
        // For objects with a label property, use the label
        safeValue = value.label;
      } else {
        // For any other object, convert to string
        safeValue = String(value);
      }
    }

    // Convert specific fields to appropriate types
    if (field === 'roleId' && safeValue !== null && safeValue !== undefined && safeValue !== '') {
      safeValue = Number(safeValue);
    } else if (field === 'id' && safeValue !== null && safeValue !== undefined && safeValue !== '') {
      safeValue = Number(safeValue);
    } else if (field === 'userId' && safeValue !== null && safeValue !== undefined && safeValue !== '') {
      safeValue = Number(safeValue);
    } else if (field === 'quantity' && safeValue !== null && safeValue !== undefined && safeValue !== '') {
      safeValue = Number(safeValue);
    }

    setData(prev => ({...prev, [field]: safeValue}));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const setFields = useCallback((fields: Partial<T>) => {
    setData(prev => ({...prev, ...fields}));
    // Clear errors for updated fields
    const fieldNames = Object.keys(fields);
    setErrors(prev => {
      const newErrors = {...prev};
      fieldNames.forEach(field => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      return newErrors;
    });
  }, []);

  const setError = useCallback((field: string, error: string) => {
    setErrors(prev => ({...prev, [field]: error}));
  }, []);

  const setErrorsCallback = useCallback((newErrors: Record<string, string>) => {
    setErrors(newErrors);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearField = useCallback((field: string) => {
    setData(prev => ({...prev, [field]: initialData[field]}));
    setErrors(prev => {
      const newErrors = {...prev};
      delete newErrors[field];
      return newErrors;
    });
  }, [initialData]);

  const validateField = useCallback((field: string, forceValidation: boolean = false): boolean => {
    if (!validationRules || !validationRules[field]) {
      return true;
    }

    const rules = validationRules[field];
    const value = data[field];

    // Check if field has a value
    const hasValue = value && (typeof value !== 'string' || value.trim().length > 0);

    // Required validation
    if (rules.required && !hasValue) {
      if (forceValidation) {
        setError(field, 'هذا الحقل مطلوب');
        return false;
      }
      return false; // Invalid but don't show error yet
    }

    // If field is empty and not required, it's valid
    if (!hasValue) {
      return true;
    }

    // Min length validation
    if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
      // Custom error messages for specific fields
      if (field === 'username') {
        setError(field, 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل');
      } else if (field === 'fullName') {
        setError(field, 'الاسم الكامل يجب أن يكون حرفين على الأقل');
      } else if (field === 'password') {
        setError(field, 'كلمة المرور يجب أن تكون 8 أحرف على الأقل');
      } else {
        setError(field, `يجب أن يكون طول النص على الأقل ${rules.minLength} أحرف`);
      }
      return false;
    }

    // Max length validation
    if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
      setError(field, `يجب أن يكون طول النص أقل من ${rules.maxLength} أحرف`);
      return false;
    }

    // Pattern validation
    if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
      // Custom error messages for specific patterns
      if (field === 'username') {
        setError(field, 'اسم المستخدم يجب أن يحتوي على أحرف وأرقام و _ فقط');
      } else if (field === 'phoneNumber') {
        setError(field, 'صيغة رقم الهاتف غير صحيحة (يجب أن يكون رقم سعودي)');
      } else if (field === 'password') {
        setError(field, 'كلمة المرور يجب أن تحتوي على حرف صغير وكبير ورقم');
      } else if (field === 'email') {
        setError(field, 'صيغة البريد الإلكتروني غير صحيحة');
      } else {
        setError(field, 'تنسيق البيانات غير صحيح');
      }
      return false;
    }

    // Custom validation
    if (rules.customValidator) {
      const customError = rules.customValidator(value);
      if (customError) {
        setError(field, customError);
        return false;
      }
    }

    // Clear any existing error if field is valid
    setErrors(prev => {
      const newErrors = {...prev};
      delete newErrors[field];
      return newErrors;
    });

    return true;
  }, [data, validationRules, setError]);

  const validate = useCallback((forceValidation: boolean = false): boolean => {
    if (!validationRules) {
      return true;
    }

    let isValid = true;
    const newErrors: Record<string, string> = {};

    Object.keys(validationRules).forEach(field => {
      const fieldValid = validateField(field, forceValidation);
      if (!fieldValid) {
        isValid = false;
      }
    });

    return isValid;
  }, [validationRules, validateField]);

  const submit = useCallback(async (onSubmit: (data: T) => Promise<void>) => {
    if (isSubmitting) {return;}

    // Clear all errors first
    clearErrors();

    // Validate form before submission with force validation
    if (!validate(true)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(data);
    } catch (error) {
      const applicationError = StandardizedErrorHandler.getInstance().handleError(error);
      setError('general', applicationError.userMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, validate, clearErrors, setError]);

  const reset = useCallback(() => {
    // Ensure we reset to clean primitive values
    const cleanInitialData: any = {};
    Object.keys(initialData).forEach(key => {
      const val = initialData[key];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        cleanInitialData[key] = String(val);
      } else {
        cleanInitialData[key] = val;
      }
    });

    setData(cleanInitialData);
    setErrors({});
    setIsSubmitting(false);
    setIsDirty(false);
    setIsValid(true);
  }, [initialData]);

  const actions: FormActions<T> = {
    setField,
    setFields,
    setError,
    setErrors: setErrorsCallback,
    clearErrors,
    clearField,
    reset,
    validate,
    validateField,
    submit
  };

  const state: FormState<T> = {
    data,
    errors,
    isSubmitting,
    isDirty,
    isValid
  };

  return [state, actions];
}

/**
 * Utility function to create form validation rules
 */
export function createValidationRules<T extends Record<string, any>> (
  rules: FormValidationRules
): FormValidationRules {
  return rules;
}

/**
 * Common validation patterns
 */
export const ValidationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^(\+?966|0)?[5-9][0-9]{8}$/,
  arabicText: /^[\u0600-\u06FF\s]+$/,
  numbersOnly: /^\d+$/,
  decimal: /^\d+(\.\d{1,2})?$/
};

/**
 * Common validation rules
 */
export const CommonValidationRules = {
  required: (fieldName: string) => ({
    required: true,
    customValidator: (value: any) => {
      if (!value || (typeof value === 'string' && !value.trim())) {
        return `${fieldName} مطلوب`;
      }
      return null;
    }
  }),

  minLength: (min: number) => ({
    minLength: min,
    customValidator: (value: any) => {
      if (typeof value === 'string' && value.length < min) {
        return `يجب أن يكون طول النص على الأقل ${min} أحرف`;
      }
      return null;
    }
  }),

  maxLength: (max: number) => ({
    maxLength: max,
    customValidator: (value: any) => {
      if (typeof value === 'string' && value.length > max) {
        return `يجب أن يكون طول النص أقل من ${max} أحرف`;
      }
      return null;
    }
  }),

  email: {
    pattern: ValidationPatterns.email,
    customValidator: (value: any) => {
      if (value && !ValidationPatterns.email.test(value)) {
        return 'تنسيق البريد الإلكتروني غير صحيح';
      }
      return null;
    }
  },

  phone: {
    pattern: ValidationPatterns.phone,
    customValidator: (value: any) => {
      if (value && !ValidationPatterns.phone.test(value)) {
        return 'تنسيق رقم الهاتف غير صحيح';
      }
      return null;
    }
  },

  arabicText: {
    pattern: ValidationPatterns.arabicText,
    customValidator: (value: any) => {
      if (value && !ValidationPatterns.arabicText.test(value)) {
        return 'يجب أن يحتوي النص على أحرف عربية فقط';
      }
      return null;
    }
  },

  positiveNumber: {
    customValidator: (value: any) => {
      const num = Number(value);
      if (value && (isNaN(num) || num <= 0)) {
        return 'يجب أن يكون الرقم أكبر من صفر';
      }
      return null;
    }
  }
};

/**
 * Utility function to create form field props
 */
export function createFieldProps<T extends Record<string, any>> (
  field: string,
  formState: FormState<T>,
  formActions: FormActions<T>
) {
  // Get the raw value from form state
  const rawValue = formState.data[field];

  // Safely handle the value and ensure it's always a string for form inputs
  const getSafeValue = (value: any) => {
    if (value === null || value === undefined) {
      return '';
    }

    // For boolean values, return as-is (don't convert to string)
    if (typeof value === 'boolean') {
      return value;
    }

    // For primitive values, convert to string
    if (typeof value !== 'object') {
      return String(value);
    }

    // For objects, extract meaningful string values
    if (value.nodeType || value.constructor?.name?.includes('HTML')) {
      return value.value || value.textContent || '';
    }

    // For objects with value property (like select options), use the value
    if (value.value !== undefined) {
      return String(value.value);
    }

    // For objects with label property, use the label
    if (value.label !== undefined) {
      return String(value.label);
    }

    // For any other object, try to extract a meaningful string
    if (value.toString && value.toString() !== '[object Object]') {
      return String(value.toString());
    }

    // Fallback to empty string for any other objects
    return '';
  };

  const safeValue = getSafeValue(rawValue);

  // For file inputs, don't set value prop (causes InvalidStateError)
  const isFileInput = field === 'images';

  // Map field names to autocomplete values for better browser integration
  const getAutocompleteValue = (fieldName: string): string | undefined => {
    const autocompleteMap: Record<string, string> = {
      username: 'username',
      email: 'email',
      password: 'new-password',
      phoneNumber: 'tel',
      fullName: 'name',
      firstName: 'given-name',
      lastName: 'family-name',
      address: 'street-address',
      city: 'address-level2',
      country: 'country',
      postalCode: 'postal-code'
    };
    return autocompleteMap[fieldName];
  };

  return {
    ...(isFileInput ? {} : {value: safeValue}),
    name: field,
    id: field,
    autoComplete: getAutocompleteValue(field),
    onChange: (event: any) => {
      // Handle file inputs specially - they pass files directly
      if (field === 'images' && Array.isArray(event)) {
        formActions.setField(field, event);
        return;
      }

      // Extract value from event if it's a React SyntheticEvent
      const value = event?.target?.value !== undefined ? event.target.value : event;
      formActions.setField(field, value);
    },
    onBlur: () => {
      // Validate field when user leaves it (show errors for empty required fields)
      // We'll handle this in the form component or use a different approach
    },
    error: formState.errors[field],
    disabled: formState.isSubmitting
  };
}

/**
 * Utility function to create form submission props
 */
export function createSubmissionProps<T extends Record<string, any>> (
  formState: FormState<T>,
  formActions: FormActions<T>
) {
  return {
    isLoading: formState.isSubmitting,
    disabled: formState.isSubmitting || !formState.isValid,
    onSubmit: formActions.submit
  };
}
